// Simple in-memory cache utility with tag-based invalidation and stale-while-revalidate support.
// NOTE: Memory only (resets on reload). Can be extended to persist layer later.

export interface CacheEntry<T = unknown> {
  data: T;
  expires: number; // hard expiration timestamp
  staleAt: number; // when considered stale (SWR window begins)
  inFlight?: Promise<T> | null;
  tags: string[];
  etag?: string;
  hits: number;
  created: number;
}

interface FetchWithCacheOptions<T> {
  key: string;
  ttl: number; // hard TTL (ms)
  swr?: number; // stale window (ms) inside ttl (default 0 => no SWR)
  force?: boolean;
  request: () => Promise<T>;
  tags?: string[];
  reuseInFlight?: boolean; // default true
  onUpdate?: (data: T) => void;
}

const registry = new Map<string, CacheEntry>();

export function getCacheEntry<T = unknown>(
  key: string,
): CacheEntry<T> | undefined {
  return registry.get(key) as CacheEntry<T> | undefined;
}

export function invalidateTags(tags: string[]): void {
  if (!tags.length) return;
  const tagSet = new Set(tags);
  for (const [key, entry] of registry.entries()) {
    if (entry.tags.some((t) => tagSet.has(t))) {
      registry.delete(key);
    }
  }
}

export function clearCache(prefix?: string): void {
  if (!prefix) {
    registry.clear();
    return;
  }
  for (const key of registry.keys()) {
    if (key.startsWith(prefix)) registry.delete(key);
  }
}

export async function fetchWithCache<T>(
  options: FetchWithCacheOptions<T>,
): Promise<T> {
  const {
    key,
    ttl,
    swr = 0,
    force = false,
    request,
    tags = [],
    reuseInFlight = true,
    onUpdate,
  } = options;

  const now = Date.now();
  const entry = registry.get(key) as CacheEntry<T> | undefined;

  // A pending entry without data is not a cache hit.
  if (!force && entry?.inFlight && reuseInFlight && entry.data === undefined) {
    return entry.inFlight as Promise<T>;
  }
  if (!force && entry && entry.data !== undefined && entry.expires > now) {
    entry.hits += 1;
    if (entry.staleAt > now || entry.inFlight) return entry.data;
    const refresh = request()
      .then((data): T => {
        if (registry.get(key) === entry) {
          entry.data = data;
          entry.expires = Date.now() + ttl;
          entry.staleAt = Date.now() + Math.max(0, ttl - swr);
          entry.inFlight = null;
          onUpdate?.(data);
        }
        return data;
      })
      .catch((): T => {
        entry.inFlight = null;
        return entry.data;
      });
    entry.inFlight = refresh;
    return entry.data;
  }
  if (!force && entry?.inFlight && reuseInFlight)
    return entry.inFlight as Promise<T>;

  const pending: CacheEntry<T> = {
    data: entry?.data as T,
    expires: 0,
    staleAt: 0,
    inFlight: null,
    tags,
    hits: entry?.hits ?? 0,
    created: entry?.created ?? now,
  };
  const inFlight = Promise.resolve()
    .then(request)
    .then((data): T => {
      // Cleared or superseded requests must not repopulate the cache.
      if (registry.get(key) === pending) {
        pending.data = data;
        pending.expires = Date.now() + ttl;
        pending.staleAt = Date.now() + Math.max(0, ttl - swr);
        pending.inFlight = null;
        onUpdate?.(data);
      }
      return data;
    })
    .catch((error: unknown): T => {
      if (registry.get(key) === pending) {
        if (entry?.data !== undefined)
          registry.set(key, { ...entry, inFlight: null });
        else registry.delete(key);
      }
      if (entry?.data !== undefined && !force) return entry.data;
      throw error;
    });
  pending.inFlight = inFlight;
  registry.set(key, pending);

  return inFlight;
}

export interface CacheStats {
  key: string;
  expiresIn: number;
  staleIn: number;
  hits: number;
  tags: string[];
  createdAgo: number;
}

export function listCacheStats(): CacheStats[] {
  const now = Date.now();
  return Array.from(registry.entries()).map(([key, e]) => ({
    key,
    expiresIn: e.expires - now,
    staleIn: e.staleAt - now,
    hits: e.hits,
    tags: e.tags,
    createdAgo: now - e.created,
  }));
}
